// src/components/AddressTypeahead.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import {
  autocompleteAddress,
  getPlaceDetails,
} from "../services/addressApi";

const Wrap = styled.div`
  position: relative;
  width: 100%;
`;

const InputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Input = styled.input`
  width: 100%;
  background: transparent;
  border: none;
  outline: none;
  color: inherit;
  font-size: inherit;
  min-width: 0; /* évite le débordement en flex */
  &::placeholder {
    opacity: 0.7;
  }
`;

const Spinner = styled.div`
  width: 14px;
  height: 14px;
  border-radius: 999px;
  border: 2px solid rgba(255, 255, 255, 0.25);
  border-top-color: rgba(255, 255, 255, 0.9);
  animation: spin 0.8s linear infinite;
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  z-index: 9999;
  background: #0f0f10;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.4);
`;

const Item = styled.button`
  width: 100%;
  text-align: left;
  padding: 10px 12px;
  border: 0;
  background: transparent;
  color: white;
  cursor: pointer;
  line-height: 1.25;

  &:hover {
    background: rgba(255, 255, 255, 0.06);
  }
  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

const ItemTitle = styled.div`
  font-weight: 600;
`;

const ItemSub = styled.div`
  opacity: 0.75;
  font-size: 12px;
  margin-top: 2px;
`;

const Empty = styled.div`
  padding: 10px 12px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 13px;
`;

function useDebounced(value, delay = 250) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

/**
 * AddressTypeahead (Photon proxy via Flask)
 *
 * Expected backend:
 * - GET /maps/autocomplete?q=...  -> { predictions: [{ description, place_id, lat, lng, postcode, city, country, street?, housenumber? }] }
 * - GET /maps/place-details?lat=...&lng=... -> { formatted_address, postal_code, city, country, lat, lng }
 *
 * Props:
 * - value: string
 * - onChange: (string) => void
 * - onSelect: (payload) => void
 * - placeholder?: string
 * - inputClassName?: string
 * - inputStyle?: object
 * - minChars?: number (default 3)
 * - debounceMs?: number (default 250)
 * - disabled?: boolean
 */
export default function AddressTypeahead({
  value,
  onChange,
  onSelect,
  placeholder = "Saisis une adresse…",
  inputClassName = "",
  inputStyle = undefined,
  minChars = 3,
  debounceMs = 250,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(false);

  const debounced = useDebounced(value, debounceMs);
  const abortRef = useRef(null);
  const wrapRef = useRef(null);

  const normalizedValue = useMemo(() => (value || "").toString(), [value]);

  // Close on outside click / Escape
  useEffect(() => {
    function onDocMouseDown(e) {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    }
    function onKeyDown(e) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  // Fetch predictions
  useEffect(() => {
    if (disabled) {
      setPredictions([]);
      setOpen(false);
      return;
    }

    const q = (debounced || "").trim();
    if (q.length < minChars) {
      setPredictions([]);
      setOpen(false);
      return;
    }

    (async () => {
      try {
        setLoading(true);

        if (abortRef.current) abortRef.current.abort();
        abortRef.current = new AbortController();

        const res = await autocompleteAddress(q, abortRef.current.signal);

        const preds = res?.data?.predictions || res?.predictions || [];
        setPredictions(Array.isArray(preds) ? preds : []);
        setOpen(true);
      } catch (e) {
        // ignore abort/network errors
      } finally {
        setLoading(false);
      }
    })();
  }, [debounced, minChars, disabled]);

  function firstPostcode(pc) {
    if (!pc) return "";
    // Photon peut renvoyer "75000;75001;..."
    if (typeof pc === "string") return pc.split(";")[0].trim();
    return "";
  }

  function renderItemLines(p) {
    const hnStreet = [p?.housenumber, p?.street].filter(Boolean).join(" ").trim();
    const pcCity = [firstPostcode(p?.postcode), p?.city].filter(Boolean).join(" ").trim();

    // fallback si backend n’envoie pas street/housenumber (ou si ce n’est pas une adresse)
    const title = hnStreet || p?.description || "";
    const sub = pcCity || "";

    return { title, sub };
  }

  async function handlePick(p) {
    setOpen(false);

    const fastPayload = {
      formatted_address: p?.description || "",
      postal_code: firstPostcode(p?.postcode || ""),
      city: p?.city || "",
      country: p?.country || "",
      lat: p?.lat ?? null,
      lng: p?.lng ?? null,
    };

    // Si on a déjà de quoi remplir, on évite un second call
    if (fastPayload.postal_code || fastPayload.city || fastPayload.country) {
      onSelect?.(fastPayload);
      onChange?.(fastPayload.formatted_address);
      return;
    }

    // Fallback reverse (normalisation)
    try {
      const res = await getPlaceDetails({
        lat: p.lat,
        lng: p.lng,
      });
      const data = res?.data || res;

      const payload = {
        formatted_address: data?.formatted_address || fastPayload.formatted_address,
        postal_code: data?.postal_code || fastPayload.postal_code,
        city: data?.city || fastPayload.city,
        country: data?.country || fastPayload.country,
        lat: data?.lat ?? fastPayload.lat,
        lng: data?.lng ?? fastPayload.lng,
      };

      onSelect?.(payload);
      onChange?.(payload.formatted_address);
    } catch {
      onSelect?.(fastPayload);
      onChange?.(fastPayload.formatted_address);
    }
  }

  return (
    <Wrap ref={wrapRef}>
      <InputRow>
        <Input
          className={inputClassName}
          style={inputStyle}
          value={normalizedValue}
          onChange={(e) => onChange?.(e.target.value)}
          onFocus={() => predictions.length && setOpen(true)}
          placeholder={placeholder}
          autoComplete="off"
          disabled={disabled}
          aria-autocomplete="list"
          aria-expanded={open}
        />
        {loading && <Spinner aria-label="loading" />}
      </InputRow>

      {open && !disabled && (
        <Dropdown role="listbox">
          {predictions.map((p) => {
            const { title, sub } = renderItemLines(p);

            return (
              <Item
                type="button"
                key={p.place_id || `${p.lat}-${p.lng}-${p.description}`}
                onClick={() => handlePick(p)}
                role="option"
              >
                <ItemTitle>{title}</ItemTitle>
                {sub && <ItemSub>{sub}</ItemSub>}
              </Item>
            );
          })}

          {!loading && predictions.length === 0 && <Empty>Aucun résultat</Empty>}
        </Dropdown>
      )}
    </Wrap>
  );
}
